import { matchPath } from 'react-router-dom';

import { type GatedPage, canOpenPage } from '@/auth/pageKeys';
import { pathOnly } from '@/auth/rolePages';
import { SETTINGS_GATES, type SettingsGateName, settingsGateNow } from '@/auth/settingsGates';

/**
 * The route gates of routes/index.tsx by path, for choosing where a role lands (auth/
 * rolePages.ts): a stored page the role can no longer open is skipped for its home instead of
 * landing on /nopermission. The gates themselves still decide when the page renders.
 *
 * Mirrors the PageGate / SettingsGate routes of routes/index.tsx; rolePages.test.tsx checks that
 * every gated route there is listed here.
 */
export const PAGE_GATE_ROUTES: readonly (readonly [string, GatedPage])[] = [
  ['/headtohead', 'headToHead'],
  ['/brandprofile/:id', 'brandProfile'],
  ['/brandprofile/:id/:flag', 'brandProfile'],
  ['/partnerProfile/:propertyId', 'partnerProfile'],
  ['/partnerProfile/:propertyId/:brandId', 'partnerProfile'],
  ['/viewActivation/:id', 'campaignView'],
  ['/viewActivity/:id', 'activityView'],
  ['/viewcontract/:id', 'contractView'],
  ['/comparison', 'comparison'],
  ['/comparison/activation/:id', 'comparison'],
  ['/comparison/view', 'comparisonView'],
  ['/comparison/competition', 'comparison'],
];

export const SETTINGS_GATE_ROUTES: readonly (readonly [string, SettingsGateName])[] = [
  ['/loginpreferences', 'loginPreferences'],
  ['/company-sso', 'companySso'],
  ['/emailtemplates', 'emailTemplates'],
  ['/addemailtemplate', 'emailTemplateCreate'],
  ['/emailtemplates/:id/edit', 'emailTemplateEdit'],
  ['/currencyexchange', 'currencyExchange'],
  ['/userlist', 'users'],
  ['/users/new', 'userCreate'],
  ['/users/:id', 'users'],
  ['/adduser', 'userCreate'],
  ['/adduser/:type/:id', 'users'],
  ['/userrolelist', 'roles'],
  ['/organisations', 'organisations'],
  ['/access-review', 'accessReview'],
  // Roles > Permissions (`alltype`) or Tiles (routes/index.tsx RolePermissionRoute)
  ['/rolepermission/alltype/:id', 'rolePermissions'],
  ['/rolepermission/:pagetype/:id', 'roleTiles'],
];

const matches = (pattern: string, path: string): boolean =>
  matchPath({ path: pattern, caseSensitive: false, end: true }, path) !== null;

/**
 * False when the active role cannot open the page `page` (`/path?query`) by the route gates
 * that decide without a call. A Settings gate that also asks the identity service, and the
 * capability-gated pages, count as openable: their gate decides on the page (and a first
 * landing it refuses goes home, auth/PageGate.tsx `RefusedRedirect`).
 */
export function canOpenPath(page: string): boolean {
  const path = pathOnly(page);
  const gated = PAGE_GATE_ROUTES.find(([pattern]) => matches(pattern, path));
  if (gated) return canOpenPage(gated[1]);
  const setting = SETTINGS_GATE_ROUTES.find(([pattern]) => matches(pattern, path));
  if (setting) {
    const name = setting[1];
    return settingsGateNow(name) || 'later' in SETTINGS_GATES[name];
  }
  return true;
}
